<template>
  <v-card class="elevation-12">
    <v-toolbar color="primary" dark>
      {{ $t('clientUnitStatisticsPage.pageTitle') }}
    </v-toolbar>

    <v-sheet color="grey lighten-3"
             class="px-4 pt-4 pb-3"
             tile>
      <h3 class="transition-swing font-weight-medium">{{ $t('statisticsTable.filterTitle') }}</h3>
      <v-responsive>
        <v-row>
          <v-col cols="12" sm="6" md="4" class="py-2">
            <v-autocomplete v-model="filters.client"
                            :items="clients"
                            :label="$t('statisticsTable.labels.organization')"
                            :dense="true"
                            item-text="name"
                            item-value="id"
                            @change="load"
            />
          </v-col>
          <v-col cols="12" sm="6" md="4" class="py-2">
            <DateInput v-model="filters.start"
                       :label="$t('statisticsTable.labels.startDate')"
                       :dense="true"
                       min-width="0"
                       max-width="80%"
                       :clearable="false"
            />
          </v-col>
          <v-col cols="12" sm="6" md="4" class="py-2">
            <DateInput v-model="filters.end"
                       :label="$t('statisticsTable.labels.endDate')"
                       :dense="true"
                       min-width="0"
                       max-width="80%"
                       :clearable="false"
            />
          </v-col>
          <v-col cols="12" sm="6" md="12" class="mt-auto mb-auto d-flex justify-space-between">
            <v-btn large color="primary" @click="load">
              {{ $t('statisticsTable.actions.update') }}
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <div>
              <v-btn large color="warning" class="me-2" dark @click="onExcelButtonClicked">{{ $t('statisticsTable.actions.toExcel') }}</v-btn>
              <v-btn large color="primary" @click="onPrintClicked">
                {{ $t('statisticsTable.actions.print') }}
                <v-icon>mdi-printer</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-responsive>
    </v-sheet>

    <v-card-text>
      <v-simple-table :dense="true">
        <template v-slot:default>
          <thead>
          <tr>
            <th rowspan="2" class="text-center font-weight-black">{{ $t('statisticsTable.headers.number') }}</th>
            <th rowspan="2" class="text-center font-weight-black">{{ $t('organizationsPage.labels.branch') }}</th>
            <th rowspan="2" class="text-center font-weight-black">{{ $t('statisticsTable.headers.acceptedForPeriod') }}</th>
            <th rowspan="2" class="text-center font-weight-black">{{ $t('statisticsTable.headers.over3Sheets') }}</th>
            <th rowspan="2" class="text-center font-weight-black">{{ $t('statisticsTable.headers.pageCount') }}</th>
            <th colspan="2" class="text-center font-weight-black">
              {{ $t('statisticsTable.headers.price') }}
            </th>
            <th class="text-center font-weight-black" rowspan="2">
              {{ $t('statisticsTable.headers.sumForRegular') }}
            </th>
            <th class="text-center font-weight-black" rowspan="2">
              {{ $t('statisticsTable.headers.sumForOver3Sheets') }}
            </th>
            <th class="text-center font-weight-black" rowspan="2">
              {{ $t('statisticsTable.headers.totalSum') }}
            </th>
          </tr>
          <tr>
            <th class="text-center font-weight-black">
              {{ $t('statisticsTable.headers.regularMail') }}
            </th>
            <th class="text-center font-weight-black">
              {{ $t('statisticsTable.headers.over3Sheets') }}
            </th>
          </tr>
          </thead>
          <tbody>
          <template v-if="displayedStats.length">
            <tr v-for="(item, index) in displayedStats" :key="index">
              <td class="text-center">{{ index + 1 }}</td>

              <td class="text-left">
                <template v-if="item.unit">
                  {{ item.unit.Name }}
                </template>
              </td>

              <td class="text-right">{{ item.arrivedCount | decimalNoTrim }}</td>

              <td class="text-right">{{ item.overPagesSum | decimalNoTrim }}</td>
              <td class="text-center">{{ item.pagesCount | decimalNoTrim }}</td>

              <td class="text-right">{{ item.priceSimple | decimalNoTrim }}</td>
              <td class="text-right">{{ item.pricePerOverPage | decimalNoTrim }}</td>

              <td class="text-right">{{ item.costSimple | decimalNoTrim }}</td>
              <td class="text-right">{{ item.costOverPage | decimalNoTrim }}</td>
              <td class="text-right">{{ item.totalCost | decimalNoTrim }}</td>
            </tr>

            <tr>
              <td class="text-center font-weight-black" colspan="2">{{ $t('statisticsTable.footer.total') }}</td>
              <td class="text-right font-weight-black">{{ totalStats.arrivedCount | decimalNoTrim }}</td>

              <td class="text-right font-weight-black">{{ totalStats.overPagesSum | decimalNoTrim }}</td>
              <td class="text-center font-weight-black">{{ totalStats.pagesCount | decimalNoTrim }}</td>

              <td class="text-right font-weight-black">{{ totalStats.priceSimple | decimalNoTrim }}</td>
              <td class="text-right font-weight-black">{{ totalStats.pricePerOverPage | decimalNoTrim }}</td>

              <td class="text-right font-weight-black">{{ totalStats.costSimple | decimalNoTrim }}</td>
              <td class="text-right font-weight-black">{{ totalStats.costOverPage | decimalNoTrim }}</td>
              <td class="text-right font-weight-black">{{ totalStats.totalCost | decimalNoTrim}}</td>
            </tr>
          </template>
          <template v-else>
            <tr>
              <td colspan="11" class="text-center">{{ $t('statisticsTable.noData') }}</td>
            </tr>
          </template>

          </tbody>
        </template>
      </v-simple-table>
    </v-card-text>
  </v-card>
</template>

<script>
import {LAYOUT_LOADING_START, LAYOUT_LOADING_STOP, LAYOUT_TITLE_CHANGED} from "@/store/modules/layout/actions";
import StatisticsService from "@/services/statistics.service";
import ClientService from "@/services/client.service";
import _ from "underscore";
import Mappers from "@/mappers";
import DateInput from "@/components/input/DateInput";
import Moment from 'moment'
import {PRICE_OVER_PAGE_FOR_HYBRID, PRICE_SIMPLE_FOR_HYBRID} from "@/contants/constants";
import XLSX from "xlsx";

// UPDATED: Constant for the specific client name
const TRIGGER_CLIENT_NAME = 'Э-маъмурий иш (EMI)';

export default {
  name: "ClientUnitStatisticsPage",
  components: {
    DateInput
  },
  data() {
    return {
      clients: [],
      stats: [],
      totalStats: {
        arrivedCount: 0,
        overPagesSum: 0,
        pagesCount: 0,
        priceSimple: '',
        pricePerOverPage: '',
        costSimple: 0,
        costOverPage: 0,
        totalCost: 0
      },
      filters: {
        client: null,
        start: Moment(),
        end: Moment(),
        isExcel: false
      }
    }
  },
  computed: {
    // UPDATED: Computed property to handle conditional grouping
    displayedStats() {
      // 1. Basic Validation
      if (!this.clients.length || !this.filters.client) {
        return this.stats;
      }

      // 2. Find selected client
      const selectedClientObj = this.clients.find(c => c.id === this.filters.client);

      // 3. Check if we should group (Strict Name Match)
      if (!selectedClientObj || selectedClientObj.name !== TRIGGER_CLIENT_NAME) {
        return this.stats;
      }

      // 4. Grouping Logic
      const groupedMap = {};

      this.stats.forEach(item => {
        if (!item.unit || !item.unit.Name) return;

        // Extract Group Name (e.g. "MILITSIYA")
        const rawName = item.unit.Name;
        const groupKey = rawName.includes(',') ? rawName.split(',')[0].trim() : rawName;

        if (!groupedMap[groupKey]) {
          groupedMap[groupKey] = {
            unit: { Name: groupKey },
            // Prices remain constant
            priceSimple: item.priceSimple,
            pricePerOverPage: item.pricePerOverPage,
            // Init counters
            arrivedCount: 0,
            overPagesSum: 0,
            pagesCount: 0,
            costSimple: 0,
            costOverPage: 0,
            totalCost: 0
          };
        }

        // Sum Numeric Fields
        groupedMap[groupKey].arrivedCount += Number(item.arrivedCount) || 0;
        groupedMap[groupKey].overPagesSum += Number(item.overPagesSum) || 0;
        groupedMap[groupKey].pagesCount += Number(item.pagesCount) || 0;
        groupedMap[groupKey].costSimple += Number(item.costSimple) || 0;
        groupedMap[groupKey].costOverPage += Number(item.costOverPage) || 0;
        groupedMap[groupKey].totalCost += Number(item.totalCost) || 0;
      });

      return Object.values(groupedMap);
    }
  },
  methods: {
    formatNumberWithSpaces(number) {
      if (number === null || number === undefined) return '';
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    onExcelButtonClicked(){
      this.filters.isExcel = true;
      this.load()
    },
    load() {
      this.$store.dispatch(LAYOUT_LOADING_START)

      this.totalStats.arrivedCount = 0
      this.totalStats.overPagesSum = 0
      this.totalStats.pagesCount = 0

      this.totalStats.costSimple = 0
      this.totalStats.costOverPage = 0
      this.totalStats.totalCost = 0

      StatisticsService.getClientUnitStatistics({
        clientId: this.filters.client,
        start: this.filters.start,
        end: this.filters.end
      })
          .then(response => {
            let sentUnits = _.filter(response.data, s => {
              return s.Count > 0
            })

            this.stats = _.map(sentUnits, s => {
              return {
                unit: s.Unit,
                arrivedCount: s.Count,
                overPagesSum: s.OverPagesSum,
                pagesCount: s.PagesCount,
                priceSimple: PRICE_SIMPLE_FOR_HYBRID,
                pricePerOverPage: PRICE_OVER_PAGE_FOR_HYBRID,
                costSimple: s.Count * PRICE_SIMPLE_FOR_HYBRID,
                costOverPage: s.OverPagesSum * PRICE_OVER_PAGE_FOR_HYBRID,
                totalCost: (s.OverPagesSum * PRICE_OVER_PAGE_FOR_HYBRID) + (s.Count * PRICE_SIMPLE_FOR_HYBRID)
              }
            })

            _.each(this.stats, s => {
              this.totalStats.arrivedCount += s.arrivedCount
              this.totalStats.overPagesSum += s.overPagesSum
              this.totalStats.pagesCount += s.pagesCount

              this.totalStats.costSimple += s.costSimple
              this.totalStats.costOverPage += s.costOverPage
              this.totalStats.totalCost += s.totalCost
            })

            if (this.filters.isExcel) {
              const workbook = XLSX.utils.book_new();
              const worksheet = XLSX.utils.aoa_to_sheet([
                [
                  this.$t('statisticsTable.headers.number'),
                  this.$t('statisticsTable.headers.branch'),
                  this.$t('statisticsTable.headers.acceptedForPeriod'),
                  this.$t('statisticsTable.headers.over3Sheets'),
                  this.$t('statisticsTable.headers.pageCount'),
                  this.$t('statisticsTable.headers.regularMail'),
                  this.$t('statisticsTable.headers.over3Sheets'),
                  this.$t('statisticsTable.headers.sumForRegular'),
                  this.$t('statisticsTable.headers.sumForOver3Sheets'),
                  this.$t('statisticsTable.headers.totalSum')
                ],
              ]);

              worksheet['!cols'] = [{ width: 10 }, { width: 20 }, { width: 15 }, { width: 15 }, { width: 20 }, { width: 15 }, { width: 15 }, { width: 20 }, { width: 25 }, { width: 20 }];
              worksheet['!rows'] = [{ hpx: 25 }];

              for (let i = 1; i <= 10; i++) {
                const cellAddress = XLSX.utils.encode_cell({ r: 0, c: i - 1 });
                const cell = worksheet[cellAddress];
                if (!cell) continue;

                cell.s = {
                  alignment: {
                    horizontal: 'center',
                    vertical: 'center',
                  },
                  fill: {
                    fgColor: { rgb: '4472C4' },
                  },
                  border: {
                    top: { style: 'thin', color: { rgb: '000000' } },
                    bottom: { style: 'thin', color: { rgb: '000000' } },
                    left: { style: 'thin', color: { rgb: '000000' } },
                    right: { style: 'thin', color: { rgb: '000000' } },
                  },
                };
              }

              // UPDATED: Generate Excel rows from displayedStats to match screen
              this.displayedStats.forEach((item, index) => {
                const row = [
                  index + 1,
                  item.unit ? item.unit.Name : '',
                  this.formatNumberWithSpaces(item.arrivedCount),
                  this.formatNumberWithSpaces(item.overPagesSum),
                  this.formatNumberWithSpaces(item.pagesCount),
                  this.formatNumberWithSpaces(item.priceSimple),
                  this.formatNumberWithSpaces(item.pricePerOverPage),
                  this.formatNumberWithSpaces(item.costSimple),
                  this.formatNumberWithSpaces(item.costOverPage),
                  this.formatNumberWithSpaces(item.totalCost)
                ];

                XLSX.utils.sheet_add_aoa(worksheet, [row], { origin: index + 1 });
              });
              
              // Add Footer row to Excel
              const footerRow = [
                '',
                this.$t('statisticsTable.footer.total'),
                this.formatNumberWithSpaces(this.totalStats.arrivedCount),
                this.formatNumberWithSpaces(this.totalStats.overPagesSum),
                this.formatNumberWithSpaces(this.totalStats.pagesCount),
                '',
                '',
                this.formatNumberWithSpaces(this.totalStats.costSimple),
                this.formatNumberWithSpaces(this.totalStats.costOverPage),
                this.formatNumberWithSpaces(this.totalStats.totalCost)
              ];
              // Calculate where to put the footer (length of displayedStats + header row + 1 buffer)
              XLSX.utils.sheet_add_aoa(worksheet, [footerRow], { origin: -1 });

              XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

              const excelBuffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
              const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', 'statistics.xlsx');
              document.body.appendChild(link);
              link.click();

              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              this.filters.isExcel = false;
            }
          })
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },
    loadClients() {
      this.$store.dispatch(LAYOUT_LOADING_START)

      return ClientService.getClientTypes({})
          .then(response => {
            this.clients = _.map(response.data, c => {
              return Mappers.mapClientType(c)
            })

            if (this.clients.length) {
              this.filters.client = this.clients[7].id
            }
          })
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },
    onPrintClicked() {
      this.loading = true;
      this.$store.dispatch(LAYOUT_LOADING_START)

      StatisticsService.printClientUnitStatistics({
        clientId: this.filters.client,
        start: this.filters.start,
        end: this.filters.end,
        isPdf: true
      })
          .then(() => { })
          .catch(() => { })
          .finally(() => {
            this.loading = false;
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },
    isClientTypeIsBank(clientId) {
      let isBank = false;
      switch (clientId) {
        case 8: isBank = true; break;
        case 11: isBank = true; break;
        case 12: isBank = true; break;
        case 13: isBank = true; break;
        case 14: isBank = true; break;
        case 15: isBank = true; break;
        case 16: isBank = true; break;
        case 22: isBank = true; break;
        case 23: isBank = true; break;
        case 24: isBank = true; break;
        case 28: isBank = true; break;
      }

      return isBank;
    }
  },
  created() {
    this.$store.dispatch(LAYOUT_TITLE_CHANGED, this.$t('clientUnitStatisticsPage.pageTitle'))

    if (this.$route.query.client)
      this.filters.client = parseInt(this.$route.query.client) // Ensure correct type

    this.loadClients()
        .then(() => {
          this.load()
        })
  }
}
</script>

<style scoped>
td, th {
  border: thin solid rgba(0, 0, 0, 0.12);
}

th {
//border: thin solid rgba(39, 22, 22, 68) !important;
}
</style>